import React from "react"
import { Box, Stack, Switch, Typography } from "@mui/material"
import { dsColorNew } from "@palette"
import useDsRepricingDataInfo from "Pages/DsRepricing/Page/@hooks/useDataInfo"
import { dsRepricingDrawerData } from "Pages/DsRepricing/Page/@signals"

// Card 7 of the scope step, "Which kinds of offer" (CU-869fh646w, UX
// decision D2): who the rule is compared against, per axis. By default
// a competitor only counts if it sells the same tariff type and the same
// insurance as our own row — like with like. Each switch turns one axis
// off, so every kind on it counts. It changes *who we compare against*
// and never which of our rows the rule prices — that is card 3, in the
// other block, which is why the two switches live here and not there
// (brief criterion 13).
//
// Wire names: `compareEveryTariffType` / `compareEveryInsurance`,
// booleans, off by default; a strategy saved before them reads back off
// (`adapters.fromServer`).
const SWITCHES = [
	{
		key: 'compareEveryTariffType',
		label: 'Compare against every tariff type',
		off: 'Only offers with the same tariff type as our row count (prepaid vs pay on arrival).',
		on: 'Prepaid and pay-on-arrival offers both count — for a page that mixes them, where you want to be first regardless of tariff type.',
	},
	{
		key: 'compareEveryInsurance',
		label: 'Compare against every insurance',
		off: 'Only offers with the same insurance as our row count.',
		on: 'Offers with any insurance count — e.g. a premium car measured against competitors sold with excess.',
	},
]

function KindSwitch({ definition }) {
	const d = dsRepricingDrawerData.peek()
	const { dataInfo, setDataInfo } = useDsRepricingDataInfo(!!d[definition.key])
	const on = !!dataInfo.value
	return (
		// The row on takes the primary tint so the state reads without
		// reading: two switches side by side are easy to misread.
		<Box sx={{ p: 1.25, borderRadius: '10px', border: '1px solid',
		           borderColor: on ? dsColorNew.noteInfoBorder : dsColorNew.grayBorder,
		           bgcolor: on ? dsColorNew.pillActiveBg : 'transparent',
		           transition: 'background-color 120ms ease, border-color 120ms ease' }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
				<Switch checked={on} onChange={e => setDataInfo(e.target.checked, definition.key)} size="small"/>
				<Typography sx={{ fontSize: 13, fontWeight: 500, color: dsColorNew.textDark }}>
					{definition.label}
				</Typography>
			</Box>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, lineHeight: 1.45,
			                  display: 'block', mt: 0.5, pl: 5.5 }}>
				{on ? definition.on : definition.off}
			</Typography>
		</Box>
	)
}

export default function DsRepricingCompareEveryKind() {
	return (
		<Stack gap={1.5}>
			{SWITCHES.map(def => <KindSwitch key={def.key} definition={def}/>)}
		</Stack>
	)
}
